import { Route, Routes, Navigate } from "react-router-dom";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import { TopBar } from "./components/layout/TopBar";
import { ParticleBackground } from "./components/ui/ParticleBackground";
import Aurora from "./components/ui/Aurora";
import Notebook from "./pages/Notebook";
import { useAuthStore } from "./store/useAuthStore";
import { API_BASE } from "./lib/api";
import { motion } from "framer-motion";

// Make sure your real Client ID is here
const GOOGLE_CLIENT_ID = "770405314894-vnfidldhkg499fanvhk5ti1hrer4ji3k.apps.googleusercontent.com";

function LoginScreen() {
  const login = useAuthStore(s => s.login);
  const handleSuccess = async (cred: any) => {
    try {
      const res = await fetch(API_BASE + "/auth/google/", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: cred.credential })
      });
      const data = await res.json();
      if (data.status === "success") {
        const payload = JSON.parse(atob(cred.credential.split('.')[1]));
        login({ name: data.user.name, email: data.user.email, token: cred.credential, picture: payload.picture });
      }
    } catch (e) { console.error("Login failed", e); }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#050505]">
      {/* Immersive Glowing Backgrounds */}
      <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
        <motion.div animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }} transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }} 
          className="absolute -top-[10%] -left-[10%] w-[50vw] h-[50vw] bg-electric/20 rounded-full blur-[120px]" />
        <motion.div animate={{ scale: [1, 1.3, 1], opacity: [0.2, 0.4, 0.2] }} transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 1 }} 
          className="absolute top-[40%] -right-[10%] w-[60vw] h-[60vw] bg-magenta/15 rounded-full blur-[150px]" />
      </div>

      <div className="z-10 container mx-auto px-6 grid xl:grid-cols-2 gap-12 items-center h-full">
        {/* Left Side: Typography & Login */}
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: "easeOut" }} className="max-w-2xl">
          
          {/* HORIZONTAL LOGO FIX */}
          <div className="h-16 sm:h-24 w-auto max-w-full mb-10 flex items-center justify-start">
            <img src="/brand-logo.png" alt="Logo" className="w-auto h-full object-contain object-left drop-shadow-2xl" />
          </div>

          <h1 className="text-6xl sm:text-7xl lg:text-8xl font-extrabold tracking-tighter mb-8 leading-[1.1]">
            Turn Ideas into <br/>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-electric via-magenta to-electric animate-gradient-x">
              Masterpieces.
            </span>
          </h1>
          <p className="text-lg sm:text-xl text-white/60 mb-12 leading-relaxed font-light">
            Experience the next generation of content creation. Instantly transform your prompts, documents, and YouTube links into fully narrated, breathtaking 3D educational videos.
          </p>
          <div className="inline-block p-1.5 rounded-full glass bg-white/5 border border-white/10 shadow-2xl hover:shadow-[0_0_30px_rgba(139,95,255,0.3)] transition-shadow duration-500">
            <GoogleLogin onSuccess={handleSuccess} onError={() => console.error("Login Failed")} theme="filled_black" shape="pill" size="large" />
          </div>
        </motion.div>

        {/* Right Side: Floating Abstract UI Cards (Restored Definition) */}
        <div className="hidden xl:flex relative h-[700px] w-full items-center justify-center perspective-[1200px]">
          <div className="absolute inset-0 border border-white/5 rounded-full animate-[spin_60s_linear_infinite]" />
          <div className="absolute inset-16 border border-electric/10 rounded-full animate-[spin_40s_linear_infinite_reverse]" />
          
          <motion.div animate={{ y: [-15, 15, -15], rotateY: [-10, -5, -10], rotateX: [5, 10, 5] }} transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }} 
            className="absolute top-10 right-20 w-72 h-80 glass rounded-[2rem] p-6 backdrop-blur-2xl border border-white/20 shadow-[0_20px_50px_rgba(0,0,0,0.5)] bg-gradient-to-br from-white/10 to-transparent flex flex-col gap-4 z-20">
            <div className="w-12 h-12 rounded-full bg-gradient-to-r from-electric to-magenta opacity-80 shadow-lg" />
            <div className="h-4 w-3/4 bg-white/10 rounded-full mt-2" />
            <div className="h-4 w-1/2 bg-white/10 rounded-full" />
            <div className="mt-auto h-32 w-full bg-black/40 rounded-xl border border-white/10 flex items-center justify-center overflow-hidden relative">
               <div className="absolute inset-0 bg-electric/20 animate-pulse" />
               <span className="text-4xl relative z-10">✨</span>
            </div>
          </motion.div>

          <motion.div animate={{ y: [15, -15, 15], rotateY: [15, 20, 15], rotateX: [-5, -10, -5] }} transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }} 
            className="absolute bottom-20 left-10 w-80 h-56 glass rounded-[2rem] p-6 backdrop-blur-3xl border border-white/20 shadow-[0_30px_60px_rgba(139,95,255,0.2)] bg-gradient-to-tr from-charcoal to-white/5 flex flex-col gap-4 z-30">
            <div className="flex items-center gap-4 mb-2">
               <div className="w-10 h-10 rounded-xl bg-electric/40 border border-electric/50 flex items-center justify-center animate-pulse"><span className="w-4 h-4 bg-white rounded-full" /></div>
               <div className="h-4 w-24 bg-white/20 rounded-full" />
            </div>
            <div className="space-y-3 mt-2">
              <div className="h-2 w-full bg-white/10 rounded-full" />
              <div className="h-2 w-5/6 bg-white/10 rounded-full" />
              <div className="h-2 w-4/6 bg-white/10 rounded-full" />
            </div>
            <div className="mt-auto flex justify-between items-center">
              <div className="flex -space-x-2">
                <div className="w-6 h-6 rounded-full bg-white/20 border border-charcoal" />
                <div className="w-6 h-6 rounded-full bg-white/30 border border-charcoal" />
              </div>
              <div className="px-4 py-1.5 rounded-full bg-gradient-to-r from-electric to-magenta text-[10px] font-bold tracking-widest shadow-glow">SYNTHESIZING</div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const user = useAuthStore(s => s.user);
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <div className="relative min-h-screen overflow-x-hidden text-white bg-[#0A0A0A] selection:bg-electric/30 font-sans">
        <ParticleBackground />
        <Aurora />
        
        {!user ? <LoginScreen /> : (
          <>
            <TopBar />
            <main className="pt-32 pb-10 min-h-screen flex justify-center items-start">
              <Routes>
                <Route path="/" element={<Navigate to="/notebook" />} />
                <Route path="/notebook" element={<Notebook />} />
              </Routes>
            </main>
          </>
        )}
      </div>
    </GoogleOAuthProvider>
  );
}
